export type FormFillerAnyone = { stage: 1; kind: 'anyone' }
export type FormFillerEmployee = {
  stage: number
  kind: 'employee'
  employeeId: string
  label: string
}
export type FormFiller = FormFillerAnyone | FormFillerEmployee

export const DEFAULT_FILLERS: FormFiller[] = [{ stage: 1, kind: 'anyone' }]

export function normalizeFillers(raw: unknown): FormFiller[] {
  const list = Array.isArray(raw) ? raw : []
  const extras: FormFillerEmployee[] = []
  for (const item of list) {
    const stage = Number((item as any)?.stage)
    if (!Number.isInteger(stage) || stage < 2) continue
    if ((item as any)?.kind !== 'employee') continue
    extras.push({
      stage,
      kind: 'employee',
      employeeId: String((item as any).employeeId || '').trim(),
      label: String((item as any).label || '').trim(),
    })
  }
  extras.sort((a, b) => a.stage - b.stage)
  return [
    { stage: 1, kind: 'anyone' },
    ...extras.map((f, i) => ({ ...f, stage: i + 2 })),
  ]
}

export function isMultiFillerForm(form: { fillers?: unknown } | null | undefined): boolean {
  return normalizeFillers(form?.fillers).length >= 2
}

export function fieldFillerStage(question: { fillerStage?: number } | null | undefined): number {
  const n = Number(question?.fillerStage)
  return Number.isInteger(n) && n >= 1 ? n : 1
}

export function isReadonlyQuestion(question: { readonly?: boolean } | null | undefined): boolean {
  return !!question?.readonly
}

/** Required for the current filler. Readonly and other-stage fields are never required. */
export function isRequiredForCurrentStage(
  question: { required?: boolean; readonly?: boolean; fillerStage?: number } | null | undefined,
  currentStage: number | null | undefined,
): boolean {
  if (!question) return false
  if (isReadonlyQuestion(question)) return false
  if (question.required === false) return false
  if (currentStage != null && fieldFillerStage(question) !== currentStage) return false
  return true
}

/** User cannot type. Tools may still fill current-stage readonly fields. */
export function isFieldInputLocked(
  question: { readonly?: boolean; fillerStage?: number } | null | undefined,
  currentStage: number | null | undefined,
): boolean {
  if (!question) return false
  if (isReadonlyQuestion(question)) return true
  if (currentStage != null && fieldFillerStage(question) !== currentStage) return true
  return false
}

/** Prefill tools run on current-stage fields, including readonly. */
export function isFieldPrefillable(
  question: { fillerStage?: number } | null | undefined,
  currentStage: number | null | undefined,
): boolean {
  if (currentStage == null) return true
  return fieldFillerStage(question) === currentStage
}

export function applyRequiredFlag<T extends { required?: boolean; readonly?: boolean }>(
  cfg: T,
  required: boolean,
): T {
  return {
    ...cfg,
    required,
    ...(required ? { readonly: false } : {}),
  }
}

export function applyReadonlyFlag<T extends { required?: boolean; readonly?: boolean }>(
  cfg: T,
  readonly: boolean,
): T {
  return {
    ...cfg,
    readonly,
    ...(readonly ? { required: false } : {}),
  }
}

export function walkFormQuestions(form: { steps?: Array<{ questions?: any[] }> } | null | undefined, visit: (q: any) => void) {
  const walk = (node: any) => {
    if (!node) return
    visit(node)
    if (node.type === 'group') {
      for (const child of node.questions || []) walk(child)
    }
  }
  for (const step of form?.steps || []) {
    for (const q of step?.questions || []) walk(q)
  }
}

export function validateFillerDefinition(form: {
  fillers?: unknown
  steps?: Array<{ questions?: any[] }>
}): string[] {
  const errors: string[] = []
  const fillers = normalizeFillers(form.fillers)
  const stages = new Set(fillers.map((f) => f.stage))

  for (const extra of fillers.slice(1) as FormFillerEmployee[]) {
    if (!extra.employeeId) {
      errors.push(`Filler stage ${extra.stage} needs an assigned employee`)
    }
  }

  const counts = new Map<number, number>()
  walkFormQuestions(form, (q) => {
    if (!q || q.type === 'group' && q.mode !== 'radio') return
    if (q.required && q.readonly) {
      errors.push(`Field "${q.label || q.name || q.id}" cannot be both required and read only`)
    }
    const stage = fieldFillerStage(q)
    if (!stages.has(stage)) {
      errors.push(`Field "${q.label || q.name || q.id}" is assigned to missing filler stage ${stage}`)
    }
    counts.set(stage, (counts.get(stage) || 0) + 1)
  })

  for (const extra of fillers.slice(1)) {
    if (!(counts.get(extra.stage) || 0)) {
      errors.push(`Filler stage ${extra.stage} has no fields assigned`)
    }
  }

  return errors
}

export function unwrapInstanceAnswers(answers: Record<string, any> | null | undefined): Record<string, any> {
  const out: Record<string, any> = {}
  if (!answers || typeof answers !== 'object') return out
  for (const [id, stored] of Object.entries(answers)) {
    if (
      stored &&
      typeof stored === 'object' &&
      Object.prototype.hasOwnProperty.call(stored, 'value') &&
      (Object.prototype.hasOwnProperty.call(stored, 'stage') || Object.prototype.hasOwnProperty.call(stored, 'at'))
    ) {
      out[id] = stored.value
    } else {
      out[id] = stored
    }
  }
  return out
}

export function collectWritableFieldIds(
  form: { steps?: Array<{ questions?: any[] }> } | null | undefined,
  currentStage: number,
): Set<string> {
  const ids = new Set<string>()
  const walk = (node: any) => {
    if (!node) return
    if (node.type === 'group') {
      if (node.mode === 'radio') {
        if (fieldFillerStage(node) === currentStage) ids.add(node.id)
        return
      }
      for (const child of node.questions || []) walk(child)
      return
    }
    if (node.id && fieldFillerStage(node) === currentStage) ids.add(node.id)
  }
  for (const step of form?.steps || []) {
    for (const q of step?.questions || []) walk(q)
  }
  return ids
}

export function fillerLabel(filler: FormFiller | undefined): string {
  if (!filler) return 'Anyone'
  if (filler.kind === 'anyone') return 'Anyone'
  return filler.label || filler.employeeId || `Stage ${filler.stage}`
}
